import './bootstrap';
import '../css/app.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import router from './router';
import App from './App.vue';
import logger from './services/logger';

// Internationalization
import i18n from './plugins/i18n';

// Toast notifications
import Toast, { POSITION } from 'vue-toastification';
import 'vue-toastification/dist/index.css';

// Create the Vue app
const app = createApp(App);

// Enable Vue DevTools in development
app.config.devtools = true;
app.config.debug = true;
app.config.performance = true;

// Setup Vue global error handling (but don't suppress errors in development)
app.config.errorHandler = (error, instance, info) => {
    logger.logVueError(error, instance?.$options.name || 'Unknown', info);
    // In development, also log to console for easier debugging
    if (process.env.NODE_ENV === 'development') {
        console.error('Vue Error:', error);
        console.error('Component:', instance?.$options.name || 'Unknown');
        console.error('Info:', info);
    }
};

// Use Pinia for state management
const pinia = createPinia();
app.use(pinia);

// Initialize theme
import { useThemeStore } from './stores/theme';
const themeStore = useThemeStore();
themeStore.initTheme();

// Use i18n for internationalization
app.use(i18n);

// Use Vue Router
app.use(router);

// Log route changes
router.beforeEach((to, from, next) => {
    logger.logRouteChange(from, to);
    next();
});

// --- Stale build chunk recovery -------------------------------------------
// After a new deploy the hashed chunk filenames change. A browser tab still
// running the PREVIOUS build will 404 when it lazy-loads a route chunk
// (e.g. "Failed to fetch dynamically imported module: .../Settings-xxxx.js").
// Recover by reloading the page once so the fresh app shell + manifest load.
const CHUNK_RELOAD_KEY = 'mx:chunk-reloaded';

function isDynamicImportError(error) {
    const msg = (error && error.message) || '';
    return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload (CSS|module)/i.test(msg);
}

function recoverFromStaleChunk(targetPath) {
    // Reload at most once per stuck navigation to avoid an infinite loop if the
    // failure is genuine (the chunk really is missing/broken).
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    console.warn('[MailerX] Stale build chunk detected, reloading to fetch the latest version.');
    window.location.assign(targetPath || window.location.href);
}

// Lazy-loaded route component failed to load
router.onError((error, to) => {
    if (isDynamicImportError(error)) {
        recoverFromStaleChunk(to?.fullPath);
    }
});

// Clear the guard after any successful navigation so a later deploy can
// trigger another one-time reload.
router.afterEach(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

// Vite emits this when a <link rel="modulepreload"> chunk fails to load
window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    recoverFromStaleChunk(window.location.pathname + window.location.search + window.location.hash);
});

// Configure toast notifications
app.use(Toast, {
    position: POSITION.TOP_RIGHT,
    timeout: 4000,
    closeOnClick: true,
    pauseOnFocusLoss: true,
    pauseOnHover: true,
    draggable: true,
    draggablePercent: 0.6,
    showCloseButtonOnHover: false,
    hideProgressBar: false,
    closeButton: "button",
    icon: true,
    rtl: false,
    transition: "Vue-Toastification__bounce",
    maxToasts: 5,
    newestOnTop: true
});

// Setup error handling (defer to avoid circular dependencies)
import('./services/errorHandler').then(({ setupErrorInterceptors, setupGlobalErrorHandler }) => {
    setupErrorInterceptors();
    setupGlobalErrorHandler(app);
}).catch(error => {
    console.error('Failed to initialize error handlers:', error);
});

// Make logger globally available
app.provide('logger', logger);
app.config.globalProperties.$logger = logger;

// Mount the app
try {
    const appElement = document.getElementById('app');
    if (!appElement) {
        console.error('App element not found!');
        logger.error('App element not found', {}, 'app');
    } else {
        app.mount('#app');
        console.log('MailerX app initialized successfully');
        logger.info('Vue application mounted successfully');
    }
} catch (error) {
    console.error('Failed to mount Vue app:', error);
    logger.error('Failed to mount Vue application', { error: error.message, stack: error.stack }, 'app');
    
    // Fallback error display
    const appElement = document.getElementById('app');
    if (appElement) {
        appElement.innerHTML = `
            <div style="padding: 2rem; text-align: center; background: #fef2f2; border: 1px solid #fecaca; border-radius: 0.5rem; margin: 2rem;">
                <h1 style="color: #dc2626; margin-bottom: 1rem;">MailerX Loading Error</h1>
                <p style="color: #7f1d1d;">There was an error loading the application.</p>
                <pre style="background: #fff; padding: 1rem; border-radius: 0.25rem; text-align: left; margin-top: 1rem; color: #dc2626;">${error.message}</pre>
            </div>
        `;
    }
}